import { toast } from 'sonner'

/**
 * Dispatches a `screenata:apply-block` window event that an open policy
 * editor listens for (see `usePolicyEditorEvents`). The editor applies the
 * edit and replies with `screenata:apply-block-ack` carrying the same
 * `blockId` and an `ok` flag.
 *
 * `policySlug` (when known) is carried on the event so the listener can
 * GUARD against applying to the wrong policy: an editor only honors an
 * event whose slug matches the policy it is showing.
 *
 * Ack handling:
 * - `ok: true`  → the edit was applied; resolve `onResult(true)`.
 * - `ok: false` → the editor RECEIVED the event but hasn't applied yet
 *   (e.g. it's branching a draft from an approved version, which takes
 *   longer than the no-editor timeout). Treat as "claimed": suppress the
 *   no-editor fallback and keep listening for the eventual `ok: true`.
 * - no ack within 300ms → no editor is open. Run `onNoEditor` (the chat
 *   uses it to open the target policy in the preview overlay) or, absent
 *   that, toast the user to open the policy.
 */
export function dispatchApplyBlock(args: {
  blockId: string
  action: 'replace' | 'insert-after' | 'delete'
  content: string
  label: string
  /** Target policy slug; omitted for legacy inline-marker edits. */
  policySlug?: string | null
  onResult: (ok: boolean) => void
  /** Called when no editor acknowledged (e.g. policy not open). */
  onNoEditor?: () => void
}) {
  const { blockId, action, content, label, policySlug, onResult, onNoEditor } =
    args
  let settled = false
  let claimed = false
  let noEditorTimer: ReturnType<typeof setTimeout> | undefined
  let giveUpTimer: ReturnType<typeof setTimeout> | undefined

  const cleanup = () => {
    window.removeEventListener('screenata:apply-block-ack', ack)
    clearTimeout(noEditorTimer)
    clearTimeout(giveUpTimer)
  }

  function ack(e: Event) {
    const detail = (e as CustomEvent).detail
    if (!detail || detail.blockId !== blockId) return
    if (detail.ok) {
      if (settled) return
      settled = true
      cleanup()
      onResult(true)
    } else {
      // Received but not applied yet — keep waiting for the real outcome.
      claimed = true
    }
  }

  window.addEventListener('screenata:apply-block-ack', ack)
  window.dispatchEvent(
    new CustomEvent('screenata:apply-block', {
      detail: { blockId, action, content, label, policySlug: policySlug ?? null },
    }),
  )

  // A matching editor may ack synchronously during dispatch; only arm the
  // timers if we're still waiting.
  if (!settled) {
    noEditorTimer = setTimeout(() => {
      if (settled || claimed) return
      settled = true
      cleanup()
      if (onNoEditor) {
        onNoEditor()
      } else {
        toast.error('No policy editor is open. Open the policy to apply edits')
      }
    }, 300)
    // Upper bound: stop listening even if a claimed apply never reports a
    // terminal result (the editor surfaces its own error toast in that case).
    giveUpTimer = setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
    }, 10_000)
  }
}
